import { useNavigate } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api } from "../lib/api";
import { PageHelp } from "../components/PageHelp";
import { PageHeader } from "../components/primitives/PageHeader";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";
import { EmptyState } from "../components/primitives/EmptyState";
import { InfoTip } from "../components/primitives/InfoTip";
import { modeLabel } from "../lib/mode-labels";
import { transformDescription, vendorLabel } from "../lib/friendly-labels";
import { ButtonLink } from "../components/primitives/ButtonLink";

interface InstanceRef {
  id: string;
  name: string;
  connectorType: string;
}

interface PipelineRow {
  id: string;
  name: string;
  enabled?: boolean;
  disabled?: boolean;
  transformId?: string;
  triggers?: Array<{ mode?: string }>;
  sourceInstance?: InstanceRef | null;
  destInstance?: InstanceRef | null;
}

/**
 * Every pipeline across clients (Build group).
 *
 * The columns are plain language throughout — what it moves, for whom,
 * active or paused. Instance names and transform ids live on the detail page,
 * where someone has already chosen to look closer.
 */
export function Pipelines() {
  const nav = useNavigate();
  const q = usePolling(() => api.listPipelines(), 30000);
  const connectorsQ = usePolling(() => api.listConnectors(), 60000);
  const transformsQ = usePolling(() => api.listTransforms(), 60000);

  if (q.status === "loading") return <Pulse />;

  const connectors = connectorsQ.data?.connectors ?? [];
  const transforms = transformsQ.data?.transforms ?? [];
  const rows: PipelineRow[] = q.data?.pipelines ?? [];
  const columns: Column<PipelineRow>[] = [
    {
      key: "name",
      label: "NAME",
      render: (r) => (
        <div>
          <div className="mono">{r.name}</div>
          <div className="run-sub">{transformDescription(r.transformId, transforms)}</div>
        </div>
      ),
    },
    {
      key: "flow",
      label: "FLOW",
      render: (r) => `${vendorLabel(r.sourceInstance?.connectorType, connectors)} → ${vendorLabel(r.destInstance?.connectorType, connectors)}`,
    },
    { key: "mode", label: "MODE", render: (r) => <Badge tone="muted">{modeLabel(r.triggers?.[0]?.mode)}</Badge> },
    {
      key: "status",
      label: "STATUS",
      render: (r) => {
        const enabled = r.enabled === true || (r.enabled == null && r.disabled !== true);
        return <Badge tone={enabled ? "ok" : "muted"}>{enabled ? "Active" : "Paused"}</Badge>;
      },
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            Build
            <InfoTip termKey="pipeline" />
          </>
        }
        title="Pipelines"
        summary={`${rows.length} ${rows.length === 1 ? "pipeline" : "pipelines"} across all clients`}
        actions={<ButtonLink to="/pipelines/new" variant="primary">New pipeline</ButtonLink>}
      />
      <PageHelp />
      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav(`/pipelines/${r.id}`)}
        empty={
          <EmptyState
            title="Nothing connected yet."
            action={<ButtonLink to="/pipelines/new">New pipeline</ButtonLink>}
          />
        }
      />
    </div>
  );
}
